'use client'

import { BankFeedsAddNewFeed } from '@repo/accounts/BankFeedsAddNewFeed'
import { BankFeedsCreatePaymentLink } from '@repo/accounts/BankFeedsCreatePaymentLink'
import { BankFeedsSubTitleModule } from '@repo/accounts/BankFeedsSubTitleModule'
import { confirm } from '@repo/ui/Confirm'
import { DataGridPro, GridPaginationModel, gridClasses, useGridApiRef } from '@mui/x-data-grid-pro'
import { DateSelection } from '@repo/ui/DateSelection'
import { ExpandMoreIcon } from '@repo/ui/ExpandMoreIcon'
import { formatCurrency } from '@repo/utils/numbers'
import { keyframes } from '@mui/material/styles'
import { NewMatterIcon } from '@repo/ui/NewMatterIcon'
import { RefreshIcon } from '@repo/ui/RefreshIcon'
import { useBankFeedsStore } from '@repo/store/useBankFeedsStore'
import { useCallback, useEffect, useState } from 'react'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material'
import { useWindowSize } from '@uidotdev/usehooks'
import { v4 as uuidv4 } from 'uuid'
import { formatDate } from '@repo/utils/time'
import MUIButton from '@mui/material/Button'
import MUIMenu from '@mui/material/Menu'
import MUIMenuItem from '@mui/material/MenuItem'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'

const startOfThisMonth = (now = new Date()) => new Date(now.getFullYear(), now.getMonth(), 1)

const endOfThisMonth = (now = new Date()) => new Date(now.getFullYear(), now.getMonth() + 1, 0)

const formatCurrencyByISO = (value: number, iso = 'GBP') => {
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  const code = String(iso || 'GBP').toUpperCase()
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'symbol'
    }).format(n)
  } catch {
    const sym: Record<string, string> = {
      GBP: '£',
      USD: '$',
      EUR: '€',
      JPY: '¥',
      AUD: '$',
      CAD: '$',
      CNY: '¥',
      CHF: 'CHF',
      SEK: 'kr',
      NOK: 'kr',
      DKK: 'kr',
      NZD: '$'
    }
    return `${sym[code] ?? ''}${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
}

const rotate = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`

export const BankFeedsTransactionsModule = ({ testData = false }: BankFeedsTransactionsModuleProps) => {
  const {
    bankFeed,
    bankFeeds,
    bankFeedsHaveChanged,
    deleteBankFeed,
    fetchAllTransactionsForBankFeed,
    fetchAllTransactionsForBankFeedAsCSV,
    fetchAllTransactionsForBankFeedAsPDF,
    fetchCurrentBankFeedBalance,
    refreshBankFeedTransactions,
    resetOspreyBankAccounts,
    setBankFeedsHaveChanged,
    setCurrentLastSync,
    setSingleBankFeed
  } = useBankFeedsStore()
  const { height: windowHeight } = useWindowSize()
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()
  const apiRef = useGridApiRef()

  const [createPaymentLinkIsOpen, setCreatePaymentLinkIsOpen] = useState(false)
  const [currentBankFeedPaymentLinkId, setCurrentBankFeedPaymentLinkId] = useState(bankFeed.plaidBankAccountId ?? null)
  const [editBankFeedIsOpen, setEditBankFeedIsOpen] = useState(false)
  const [endDate, setEndDate] = useState<Date | null>(() => endOfThisMonth())
  const [linkAnchorEl, setLinkAnchorEl] = useState<null | HTMLElement>(null)
  const [loading, setLoading] = useState(false)
  const [pageSize, setPageSize] = useState<number | null>(25)
  const [startAtIndex, setStartAtIndex] = useState<number | null>(0)
  const [startDate, setStartDate] = useState<Date | null>(() => startOfThisMonth())
  const [tableHeight, setTableHeight] = useState(windowHeight ? windowHeight - 216 : 0)
  const [transactionTableData, setTransactionTableData] = useState<any>(null)

  const navigate = useNavigate()
  const theme = useTheme()

  const openLinkPopover = Boolean(linkAnchorEl)
  const closeLinkPopover = () => setLinkAnchorEl(null)

  const handleNavigateBack = () => {
    navigate('/accounts/banks-journals/bank-feeds')
  }

  const handleDeleteBankFeed = async (plaidBankAccountId: string) => {
    const proceed = await confirm(
      'Are you sure you want to delete this bank feed? This will remove the account and you will no longer be able to see transactions or create payment links.',
      { delete: true }
    )

    if (!proceed) return

    if (proceed) {
      try {
        await resetOspreyBankAccounts()
        await deleteBankFeed({ plaidBankAccountId: plaidBankAccountId })
        await setBankFeedsHaveChanged()
        closeLinkPopover()
        handleNavigateBack()
      } catch (error) {
        console.error('Error deleting bank feed:', error)
      }
    }
  }

  const handleDownloadAsCSV = useCallback(async () => {
    try {
      closeLinkPopover()

      await fetchAllTransactionsForBankFeedAsCSV({
        plaidBankAccountId: bankFeed.plaidBankAccountId,
        startAtIndex: startAtIndex ?? 0,
        pageSize: pageSize ?? 25,
        startDate: startDate ? formatDate(startDate, 'yyyy-MM-dd') : null,
        endDate: endDate ? formatDate(endDate, 'yyyy-MM-dd') : null
      })
    } catch (error) {
      console.error('Error downloading transactions as CSV:', error)
    }
  }, [bankFeed, endDate, fetchAllTransactionsForBankFeedAsCSV, pageSize, startAtIndex, startDate])

  const handleDownloadAsPDF = useCallback(async () => {
    try {
      closeLinkPopover()

      await fetchAllTransactionsForBankFeedAsPDF({
        plaidBankAccountId: bankFeed.plaidBankAccountId,
        startAtIndex: startAtIndex ?? 0,
        pageSize: pageSize ?? 25,
        startDate: startDate ? formatDate(startDate, 'yyyy-MM-dd') : null,
        endDate: endDate ? formatDate(endDate, 'yyyy-MM-dd') : null
      })
    } catch (error) {
      console.error('Error downloading transactions as CSV:', error)
    }
  }, [bankFeed, endDate, fetchAllTransactionsForBankFeedAsPDF, pageSize, startAtIndex, startDate])

  const handleRefreshTransactions = useCallback(async () => {
    try {
      const response = await refreshBankFeedTransactions({ plaidBankAccountId: bankFeed.plaidBankAccountId })

      if (response.status === 200) {
        setLoading(true)
        try {
          const data = await fetchAllTransactionsForBankFeed({
            plaidBankAccountId: bankFeed.plaidBankAccountId,
            startAtIndex: startAtIndex ?? 0,
            pageSize: pageSize ?? 25,
            startDate: startDate ? formatDate(startDate, 'yyyy-MM-dd') : null,
            endDate: endDate ? formatDate(endDate, 'yyyy-MM-dd') : null
          })
          setTransactionTableData(data)
        } catch (error) {
          console.error('Error fetching transactions for bank feed:', error)
        } finally {
          setLoading(false)
        }
      }
    } catch (error) {
      console.error('Error refreshing bank feed transactions:', error)
    }
  }, [
    bankFeed,
    refreshBankFeedTransactions,
    fetchAllTransactionsForBankFeed,
    startAtIndex,
    pageSize,
    startDate,
    endDate
  ])

  useEffect(() => {
    if (!bankFeedsHaveChanged || testData) return

    const lastSegmentFromLocation = () => {
      const { hash, pathname } = window.location

      const pathLike = hash && hash.startsWith('#') ? hash.slice(1) : pathname
      const clean = pathLike.split('?')[0].split('#')[0].replace(/\/+$/, '')
      const parts = clean.split('/').filter(Boolean)

      return parts.at(-1) || ''
    }

    const id = lastSegmentFromLocation()

    if (Array.isArray(bankFeeds) && id) {
      const match = bankFeeds.find((b) => String(b.plaidBankAccountId) === id) ?? null
      setSingleBankFeed({ bankFeed: match })
    }
  }, [bankFeeds, bankFeedsHaveChanged])

  useEffect(() => {
    if (testData) return

    const fetchTransactionTableData = async () => {
      if (!bankFeed) return

      setLoading(true)
      try {
        const data = await fetchAllTransactionsForBankFeed({
          plaidBankAccountId: bankFeed.plaidBankAccountId,
          startAtIndex: startAtIndex ?? 0,
          pageSize: pageSize ?? 25,
          startDate: startDate ? formatDate(startDate, 'yyyy-MM-dd') : null,
          endDate: endDate ? formatDate(endDate, 'yyyy-MM-dd') : null
        })
        setTransactionTableData(data)
      } catch (error) {
        console.error('Error fetching transactions for bank feed:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchTransactionTableData()
  }, [fetchAllTransactionsForBankFeed, startAtIndex, pageSize, startDate, endDate])

  useEffect(() => {
    if (!bankFeed || testData) return

    const fetchCurrentBalance = async () => {
      try {
        await fetchCurrentBankFeedBalance({ plaidBankAccountId: bankFeed.plaidBankAccountId })
      } catch (error) {
        console.error('Error fetching current balance for bank feed:', error)
      }
    }

    fetchCurrentBalance()
  }, [fetchCurrentBankFeedBalance, bankFeed.plaidBankAccountId])

  useEffect(() => {
    if (!bankFeed || testData) return

    if (!bankFeed.lastSync) return

    const getLastSyncDifference = (lastSyncValue: string | number | Date) => {
      if (!lastSyncValue) {
        return { value: null as number | null, unit: '', label: '' }
      }

      const last = new Date(lastSyncValue).getTime()
      if (Number.isNaN(last)) {
        return { value: null as number | null, unit: '', label: '' }
      }

      const now = Date.now()
      const diffMs = now - last
      if (diffMs < 0) {
        return { value: null as number | null, unit: '', label: '' }
      }

      const diffSeconds = Math.floor(diffMs / 1000)
      if (diffSeconds < 60) {
        const unit = diffSeconds === 1 ? 'second' : 'seconds'
        return { value: diffSeconds, unit, label: `${diffSeconds} ${unit} ago` }
      }

      const diffMinutes = Math.floor(diffSeconds / 60)
      if (diffMinutes < 60) {
        const unit = diffMinutes === 1 ? 'minute' : 'minutes'
        return { value: diffMinutes, unit, label: `${diffMinutes} ${unit} ago` }
      }

      const diffHours = Math.floor(diffMinutes / 60)
      if (diffHours < 24) {
        const unit = diffHours === 1 ? 'hour' : 'hours'
        return { value: diffHours, unit, label: `${diffHours} ${unit} ago` }
      }

      const diffDays = Math.floor(diffHours / 24)
      const unit = diffDays === 1 ? 'day' : 'days'
      return { value: diffDays, unit, label: `${diffDays} ${unit} ago` }
    }

    const updateLabel = () => {
      const { label } = getLastSyncDifference(bankFeed.lastSync)
      setCurrentLastSync({
        plaidBankAccountId: bankFeed.plaidBankAccountId,
        currentLastSync: label
      })
    }

    updateLabel()

    const intervalId: ReturnType<typeof setInterval> = setInterval(updateLabel, 60_000)

    return () => clearInterval(intervalId)
  }, [bankFeed?.plaidBankAccountId, bankFeed?.lastSync, setCurrentLastSync])

  const columns: any = [
    {
      field: 'date',
      headerName: 'Date',
      id: 'date',
      width: 130,
      flex: 0,
      type: 'string',
      renderCell: (params: any) => {
        const date = new Date(params.value)
        return <span>{date.toLocaleDateString()}</span>
      }
    },
    {
      field: 'paymentChannel',
      headerName: 'Type',
      id: 'paymentChannel',
      width: 120,
      flex: 0,
      type: 'string',
      renderCell: (params: any) => {
        const paymentChannel = params.value
        return <span>{paymentChannel.charAt(0).toUpperCase() + paymentChannel.slice(1)}</span>
      }
    },
    {
      field: 'name',
      headerName: 'Transaction reference',
      id: 'name',
      width: 150,
      flex: 1,
      type: 'string',
      renderCell: (params: any) => {
        const pending = params?.row?.pending
        return <span>{pending ? `${params.value ? params.value + ' ' : ''}(Pending)` : params.value}</span>
      }
    },
    {
      field: 'isoCurrencyCode',
      headerName: 'Currency',
      id: 'isoCurrencyCode',
      width: 100,
      flex: 0,
      type: 'string'
    },
    {
      field: 'amountIn',
      headerName: 'In',
      width: 130,
      flex: 0,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      renderCell: (params: any) => {
        const raw = params?.row?.amount
        const code = params?.row?.isoCurrencyCode ?? 'GBP'
        const n = typeof raw === 'number' ? raw : Number(raw)
        if (!Number.isFinite(n) || n >= 0) return ''
        return <span>{formatCurrencyByISO(Math.abs(n), code)}</span>
      }
    },
    {
      field: 'amountOut',
      headerName: 'Out',
      width: 130,
      flex: 0,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      renderCell: (params: any) => {
        const raw = params?.row?.amount
        const code = params?.row?.isoCurrencyCode ?? 'GBP'
        const n = typeof raw === 'number' ? raw : Number(raw)
        if (!Number.isFinite(n) || n <= 0) return ''
        return <span>- {formatCurrencyByISO(n, code)}</span>
      }
    }
  ]

  useEffect(() => {
    const offset = pageTitleBlockIsVisible ? 216 : 146
    if (windowHeight) setTableHeight(windowHeight - offset)
  }, [windowHeight, pageTitleBlockIsVisible])

  const paginationModel: GridPaginationModel = {
    page: Math.floor((startAtIndex ?? 0) / (pageSize ?? 25)),
    pageSize: pageSize ?? 25
  }

  console.log({ startDate })

  return (
    <MUIStack data-testid="BankFeedsTransactionsModule" direction="column" spacing={2} sx={{ width: '100%' }}>
      {!bankFeed ? (
        <MUIStack direction="column" alignItems="center" sx={{ m: '10rem 0 0 0 !important' }}>
          <NewMatterIcon sx={{ height: '5rem', width: '5rem' }} />
          <MUITypography variant="h5" sx={{ mt: '3rem' }}>
            This bank feed does not exist.
          </MUITypography>
          <MUITypography variant="h5" sx={{ mt: '0' }}>
            Please return to the bank feeds page.
          </MUITypography>
          <MUIButton onClick={handleNavigateBack} variant="contained" sx={{ mt: '2rem' }}>
            Back to bank feeds
          </MUIButton>
        </MUIStack>
      ) : (
        <MUIPaper
          sx={{
            height: 'auto',
            width: 'calc(100% - 8rem)',
            p: '2rem 2rem 2rem 1.5rem',
            m: '0 4rem 2.5rem !important',
            backgroundColor: theme.palette.primary.contrastText
          }}
        >
          <MUIStack direction="row" spacing={2} alignItems="flex-start" justifyContent={'space-between'} sx={{ mb: 2 }}>
            <MUIStack direction="column" spacing={0}>
              <MUIButton
                variant="outlined"
                onClick={handleNavigateBack}
                sx={{ ml: '1rem !important', mb: '1rem', width: 'fit-content' }}
              >
                <ExpandMoreIcon
                  sx={{
                    height: '12px',
                    width: '12px',
                    mr: '0.5rem',
                    transform: 'rotate(90deg)',
                    transition: 'transform 0.2s ease-in-out',
                    color: theme.palette.primary.main
                  }}
                />
                Back
              </MUIButton>
              <MUITypography variant="h3" sx={{ pl: 4, mb: 1 }}>
                {bankFeed.ospreyName}
              </MUITypography>
              <BankFeedsSubTitleModule bankFeed={bankFeed} />
            </MUIStack>
            <MUIButton
              variant="contained"
              onClick={(e) => setLinkAnchorEl(e.currentTarget)}
              sx={{ mr: '0.5rem !important' }}
            >
              Actions
              <ExpandMoreIcon
                sx={{
                  height: '12px',
                  width: '12px',
                  ml: '0.75rem',
                  transform: openLinkPopover ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease-in-out'
                }}
              />
            </MUIButton>
          </MUIStack>
          <hr
            style={{
              width: 'calc(100% - 1.5rem)',
              margin: '1rem 3rem 1rem 1rem',
              border: '1px solid rgba(5, 28, 51, 0.06)'
            }}
          />
          <MUIStack direction="row" spacing={2} alignItems="flex-start" justifyContent={'space-between'} sx={{ mb: 0 }}>
            <MUIStack direction="column" spacing={0}>
              <MUIStack direction="row" spacing={2} alignItems="center" justifyContent={'flex-start'} sx={{ mb: 0 }}>
                <MUITypography variant="h6" sx={{ p: '0 1rem', textTransform: 'uppercase' }}>
                  Last sync
                </MUITypography>
                {/*
               <MUIButton
                  onClick={() => handleRefreshTransactions()}
                  variant="text"
                  sx={{ height: '1.25rem', width: '1.25rem', minWidth: 'unset', m: '0 !important' }}
                >
                  <RefreshIcon
                    sx={{
                      height: '1.25rem',
                      width: '1.25rem',
                      animation: loading ? `${rotate} 1s linear infinite` : 'none'
                    }}
                  />
                </MUIButton>
                */}
              </MUIStack>
              <MUITypography variant="body2" sx={{ p: '0rem 1rem' }}>
                {bankFeed.currentLastSync || bankFeed.lastSync || 'Not synced yet'}
              </MUITypography>
            </MUIStack>
            <MUIStack direction="column" spacing={0}>
              <MUITypography variant="h6" sx={{ p: '0 0.5rem 0 1rem', m: 0, textTransform: 'uppercase' }}>
                Current Balance
              </MUITypography>
              <MUITypography
                variant="body2"
                sx={{
                  p: '0 0.5rem 0 1rem',
                  color:
                    bankFeed.currentBalance && bankFeed.currentBalance < 0
                      ? theme.palette.error.main
                      : theme.palette.primary.main
                }}
              >
                {formatCurrency(bankFeed.currentBalance)}
              </MUITypography>
            </MUIStack>
          </MUIStack>
        </MUIPaper>
      )}
      <MUIStack
        direction="row"
        spacing={8}
        alignItems="center"
        justifyContent={'flex-end'}
        sx={{ m: '0 4rem 2rem !important' }}
      >
        <DateSelection
          value={startDate}
          onChange={setStartDate}
          sx={{ width: '240px', mb: 0 }}
          label="Start Date"
          name="startDate"
          clearable={false}
          enableTypedDdMmYyyy={true}
          showAdornment={true}
        />
        <DateSelection
          value={endDate}
          onChange={setEndDate}
          sx={{ width: '240px', mb: 0 }}
          label="End Date"
          name="endDate"
          clearable={false}
          enableTypedDdMmYyyy={true}
          showAdornment={true}
        />
      </MUIStack>
      <MUIPaper
        sx={{
          position: 'relative',
          fontSize: '0.875rem',
          height: `calc(${tableHeight}px - 20px)` || 'auto',
          width: 'calc(100% - 8rem)',
          m: '0 4rem 4rem !important',
          backgroundColor: theme.palette.primary.contrastText,
          [`@media (min-height: 991px)`]: { mt: '-3.5rem' }
        }}
      >
        <DataGridPro
          apiRef={apiRef}
          autosizeOnMount
          columns={columns}
          disableAutosize
          disableColumnFilter
          disableColumnMenu
          disableColumnPinning
          disableColumnReorder
          disableColumnResize
          disableColumnSelector
          disableColumnSorting
          disableMultipleColumnsFiltering
          disableMultipleColumnsSorting
          disableMultipleRowSelection
          disableRowSelectionOnClick={false}
          disableVirtualization
          getRowId={(row) => `${row.transactionId}-${uuidv4()}`}
          loading={loading}
          onPaginationModelChange={(newModel) => {
            if (newModel.page !== paginationModel.page || newModel.pageSize !== paginationModel.pageSize) {
              setPageSize(newModel.pageSize)
              setStartAtIndex(newModel.page * newModel.pageSize)
            }
          }}
          pageSizeOptions={[5, 10, 25, 50]}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          rowCount={transactionTableData?.totalRowCount ?? 0}
          rows={Array.isArray(transactionTableData?.rows) ? transactionTableData.rows : []}
          slotProps={{
            loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' }
          }}
          sx={{
            [`.MuiDataGrid-root`]: { border: 'none' },
            [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
            [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: { outline: 'none' },
            [`* .${gridClasses.columnHeader}`]: { fontSize: '0.875rem' },
            [`& .MuiDataGrid-cell`]: { alignContent: 'center', [`& .MuiTypography-root`]: { top: 'unset' } },
            [`& .MuiTablePagination-selectLabel`]: { display: 'block !important' },
            [`& .MuiTablePagination-input`]: {
              display: 'inline-flex !important',
              mb: 0,
              ml: 0,
              mt: '8px',
              fontSize: '0.875rem',
              [`& .MuiTablePagination-select`]: {
                padding: '0 !important',
                ml: '4px',
                mr: '20px',
                minWidth: 'none',
                [`&:focus`]: { backgroundColor: 'transparent' }
              },
              [`& .MuiSvgIcon-root`]: { width: '1.5rem', height: '1.5rem', top: 'calc(50% - 12px)' }
            }
          }}
          slots={{
            headerFilterMenu: null
          }}
        />
      </MUIPaper>
      <MUIMenu
        open={openLinkPopover}
        anchorEl={linkAnchorEl}
        onClose={closeLinkPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ [`& .MuiPaper-root`]: { minWidth: '15rem !important' } }}
      >
        <MUIMenuItem
          sx={{ textTransform: 'unset', textAlign: 'left', alignSelf: 'flex-start', width: '100%' }}
          onClick={handleDownloadAsCSV}
        >
          Download as CSV
        </MUIMenuItem>
        <MUIMenuItem
          sx={{ textTransform: 'unset', textAlign: 'left', alignSelf: 'flex-start' }}
          onClick={handleDownloadAsPDF}
        >
          Download as PDF
        </MUIMenuItem>
        <MUIMenuItem
          onClick={() => {
            setCreatePaymentLinkIsOpen(true)
            closeLinkPopover()
          }}
          sx={{ textTransform: 'unset', textAlign: 'left', alignSelf: 'flex-start' }}
          disabled={!bankFeed.plaidRecipientId}
        >
          Create payment link
        </MUIMenuItem>
        <hr
          style={{
            width: 'calc(100% - 2rem)',
            margin: '1rem 1rem',
            border: '1px solid rgba(5, 28, 51, 0.06)'
          }}
        />
        <MUIMenuItem
          sx={{ textTransform: 'unset', textAlign: 'left', alignSelf: 'flex-start' }}
          onClick={() => {
            setEditBankFeedIsOpen(true)
            closeLinkPopover()
          }}
        >
          Edit
        </MUIMenuItem>
        <MUIMenuItem
          sx={{ textTransform: 'unset', textAlign: 'left', alignSelf: 'flex-start', color: theme.palette.error.main }}
          onClick={() => handleDeleteBankFeed(bankFeed.plaidBankAccountId)}
        >
          Delete
        </MUIMenuItem>
      </MUIMenu>
      <BankFeedsAddNewFeed
        bankFeed={bankFeed}
        editMode={true}
        isOpen={editBankFeedIsOpen}
        setOpen={setEditBankFeedIsOpen}
      />
      <BankFeedsCreatePaymentLink
        bankFeed={bankFeed}
        currentBankFeedPaymentLinkId={currentBankFeedPaymentLinkId}
        isOpen={createPaymentLinkIsOpen}
        setCurrentBankFeedPaymentLinkId={setCurrentBankFeedPaymentLinkId}
        setOpen={setCreatePaymentLinkIsOpen}
      />
    </MUIStack>
  )
}

type BankFeedsTransactionsModuleProps = {
  testData?: boolean
}
